import * as React from 'react';
import { css } from '@emotion/core';
import Modal from '../layout/modal/Modal';
import ModalHeader from '../layout/modal/ModalHeader';
import LocalDiv from '../localization/LocalDiv';
import Msg from '../localization/Msg';
import { Str } from '../localization/translationKeys';
import Spacer from '../layout/Spacer';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useAppState } from '../core/redux/hooks';
import { FLEX_COLUMN, FLEX_ROW } from '../layout/flexBox';
import { onMobileDevice } from '../globalStyle/mediaQueries';
import { COLORS } from '../globalStyle/colors';
import { btnStyle } from '../forms/styles/button';
import { useProjMsgText } from '../project/messages/hooks';
import { ProjStr } from '../project/messages/ProjStr';
import { useToggle } from '../core/hooks/useToggle';
import ProjMsg from '../project/messages/ProjMsg';
import { updateConfidentialityAction } from './updateConfidentialityAction';
import { useLoading, useError } from '../core/api/hooks';
import { ActionType } from '../core/redux/actionType';
import { Loader } from '../loading/Loader';
import ErrorMsg from '../error/ErrorMsg';
import { useRoutes } from '../navigation/routes';


const BTN_STYLE = css`width: 145px;`;

const styles = {
  modal:         [
    FLEX_COLUMN,
    css`
      min-height: 0;
      max-width: 700px;
      
      ${onMobileDevice}  {
        height: 100vh;
      }
    `,
  ],
  root:          [
    FLEX_COLUMN,
    css`
      flex-grow: 1;
      align-items: stretch;
      padding-left: 24px;
      padding-right: 24px;
      padding-bottom: 24px;
      color: ${COLORS.DARK_GREY};
    `,
  ],
  loader:        css`
    width: 100px; 
    height: 100px;
    margin: auto;
  `,
  agreementText: css`
    max-height: 320px;
    line-height: 14px;
    overflow: auto;
    margin-bottom: 25px;
  `,
  acceptRow:     [
    FLEX_ROW,
    css`
      align-items: center;
      font-size: 13px;
    `,
  ],
  checkbox:      css`
    width: 22px;
    height: 22px;
  `,
  checkboxDiv:      css`
    flex-direction: column;
    justify-content: center;
    align-items: center;
  `,
  error:         css`
    margin-top: 20px;
  `,
  buttonRow:     [
    FLEX_ROW,
    css`
      justify-content: flex-end;
      margin-top: 40px;
    `,
  ],
  submitButton:  [
    btnStyle.primary(COLORS.BLUE),
    BTN_STYLE,
  ],
  cancelButton:  [
    btnStyle.secondary(COLORS.BLUE),
    BTN_STYLE,
    css`
      background-color: transparent;
    `,
  ],
};


type Props = {
  className?: string;
};


/**
 * ConfidentialityAgreementModal: a component that
 *
 * PureComponent: true
 * Redux Connected: false
 */
const ConfidentialityAgreementModal: React.FC<Props> = props => {
  const title = useProjMsgText(ProjStr.ConfidentialityMessageTitle);

  const history  = useHistory();
  const dispatch = useDispatch();

  const participantId = useAppState(state => state.auth.currentUser?.personId ?? -1);
  const projectId     = useAppState(state => state.projectSummary?.projectId ?? -1);
  const routes        = useRoutes();

  const loading = useLoading(ActionType.UpdateConfidentialityRequest);
  const error   = !!useError(ActionType.UpdateConfidentialityRequest);

  const [isAccepted, toggleAccepted] = useToggle(false);

  const dismiss = React.useCallback(() => history.replace(routes.Login), [routes, history]);

  const onSubmit = React.useCallback(
    () => { dispatch(updateConfidentialityAction(participantId, projectId)); },
    [participantId, projectId, dispatch],
  );

  return (
    <Modal onDismiss={dismiss} dismissable={true} css={styles.modal}>
      <ModalHeader title={title} onDismiss={dismiss} />
      <LocalDiv css={styles.root}>
        {
          loading
            ? <Loader css={styles.loader} />
            : (
              <>
                {/* Agreement Text */}
                <ProjMsg str={ProjStr.ConfidentialityMessageAgreement} css={styles.agreementText} />

                {/* Accept Checkbox */}
                <LocalDiv css={styles.acceptRow}>
                  <div css={styles.checkboxDiv}>
                    <input type='checkbox' checked={isAccepted} onChange={toggleAccepted} css={styles.checkbox} />
                  </div>
                  <Spacer sizePx={5} />
                  <ProjMsg str={ProjStr.ConfidentialityMessageAcknowledgement} onClick={toggleAccepted} />
                </LocalDiv>

                <ErrorMsg error={error ? Str.LoadFailed : undefined} css={styles.error} />

                {/* Cancel/Submit buttons */}
                <LocalDiv css={styles.buttonRow}>
                  <button onClick={dismiss} css={styles.cancelButton}><Msg str={Str.Cancel} /></button>
                  <Spacer sizePx={12} />
                  <button
                    type='submit'
                    onClick={onSubmit}
                    css={styles.submitButton}
                    disabled={!isAccepted}
                    aria-disabled={!isAccepted}
                  >
                    <Msg str={Str.Yes} />
                  </button>
                </LocalDiv>
              </>
            )
        }
      </LocalDiv>
    </Modal>
  );
};

export default ConfidentialityAgreementModal;
